import { z } from "zod"

/** Reports (screens 36–38). Definitions are explicit and exported with the data; missing signals are null, never zero. */

export const ReportPeriod = z.enum(["7", "30"])
export type ReportPeriod = z.infer<typeof ReportPeriod>
export const ReportChannel = z.enum(["all", "instagram", "messenger"])
export type ReportChannel = z.infer<typeof ReportChannel>
export const ReportQuery = z.object({ period: ReportPeriod.default("7"), channel: ReportChannel.default("all") })
export type ReportQuery = z.infer<typeof ReportQuery>

const Metric = z.object({ label: z.string(), value: z.string(), detail: z.string(), change: z.string().nullable() })
export type ReportMetric = z.infer<typeof Metric>

export const SupportReport = z.object({
  period: ReportPeriod,
  channel: ReportChannel,
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  metrics: z.array(Metric),
  series: z.array(z.object({ day: z.string(), label: z.string(), value: z.number().int() })),
  response: z.object({ firstHumanReplyMedianSeconds: z.number().nullable(), firstHumanReplyP90Seconds: z.number().nullable(), resolutionMedianSeconds: z.number().nullable(), resolutionP90Seconds: z.number().nullable(), sampleSize: z.number().int() }),
  team: z.array(z.object({ name: z.string(), resolved: z.number().int(), humanResponseMedianSeconds: z.number().nullable(), openAtEnd: z.number().int() })),
  definitions: z.array(z.string()),
})
export type SupportReport = z.infer<typeof SupportReport>

export const AutomationReport = z.object({
  period: ReportPeriod,
  channel: ReportChannel,
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  metrics: z.array(Metric),
  funnel: z.object({ automationName: z.string().nullable(), stages: z.array(z.object({ label: z.string(), value: z.number().int() })) }),
  attention: z.array(z.object({ label: z.string(), value: z.number().int() })),
  attentionTotal: z.number().int(),
  byAutomation: z.array(z.object({ automationId: z.uuid(), name: z.string(), started: z.number().int(), completed: z.number().int(), handedOver: z.number().int() })),
  active: z.number().int(),
  definitions: z.array(z.string()),
})
export type AutomationReport = z.infer<typeof AutomationReport>

export const CampaignReport = z.object({
  campaignId: z.uuid(),
  name: z.string(),
  provider: z.enum(["instagram", "messenger"]).nullable(),
  sentAt: z.iso.datetime().nullable(),
  metrics: z.array(Metric),
  outcomes: z.array(z.object({ key: z.string(), label: z.string(), value: z.number().int(), share: z.number() })),
  eligible: z.number().int(),
  replies: z.object({ unique: z.number().int(), delivered: z.number().int(), rate: z.number().nullable() }),
  checks: z.object({ selected: z.number().int(), excluded: z.number().int(), skipped: z.number().int() }),
  signals: z.array(z.object({ label: z.string(), value: z.string().nullable() })),
  definitions: z.array(z.string()),
})
export type CampaignReport = z.infer<typeof CampaignReport>

export const CampaignReportOption = z.object({ id: z.uuid(), name: z.string(), status: z.string(), sentAt: z.iso.datetime().nullable() })
export type CampaignReportOption = z.infer<typeof CampaignReportOption>
